import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Optional label shown in the error card so we know which boundary fired. */
  label?: string;
}

interface State {
  error: Error | null;
  info: string;
}

/**
 * Generic React error boundary — permanent production safety net.
 * Catches synchronous render errors in any descendant and shows a
 * user-friendly card instead of a blank screen. Technical details are
 * collapsed by default so they don't alarm regular users but remain
 * available for debugging / bug reports.
 *
 * Usage:
 *   <ErrorBoundary label="CharacterSheetPage">
 *     <SomeComponent />
 *   </ErrorBoundary>
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: '' };

  static getDerivedStateFromError(error: Error): State {
    return { error, info: '' };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    console.error('[ErrorBoundary]', error, info.componentStack);
    this.setState({ error, info: info.componentStack });
  }

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="max-w-3xl mx-auto mt-8 space-y-4">
        <div className="card border-danger/30 space-y-4">
          {/* User-facing message */}
          <div className="space-y-1">
            <p className="font-display text-lg text-danger">Something went wrong</p>
            <p className="text-parchment-muted text-sm">
              This page encountered an unexpected error. You can try reloading, or go back to the
              previous page. If the problem persists, please report it using the feedback button.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => this.setState({ error: null, info: '' })}
              className="px-3 py-1.5 text-xs bg-gold/10 border border-gold/30 text-gold
                         hover:bg-gold/20 hover:border-gold/50 rounded transition-colors"
            >
              Try again
            </button>
            <button
              onClick={() => window.history.back()}
              className="px-3 py-1.5 text-xs border border-darkbg-border text-parchment-muted/60
                         hover:text-parchment hover:border-parchment-muted/40 rounded transition-colors"
            >
              Go back
            </button>
          </div>

          {/* Technical details — collapsed by default */}
          <details className="border-t border-darkbg-border pt-3">
            <summary className="text-parchment-muted/40 text-xs cursor-pointer select-none
                                hover:text-parchment-muted/70 transition-colors">
              Technical details
            </summary>
            <div className="mt-3 space-y-2">
              <pre className="text-danger/70 text-xs font-mono whitespace-pre-wrap break-all
                              bg-darkbg rounded p-3">
                {error.message}
              </pre>
              {info && (
                <pre className="text-parchment-muted/40 text-xs overflow-x-auto whitespace-pre-wrap
                                bg-darkbg rounded p-3">
                  {info}
                </pre>
              )}
            </div>
          </details>
        </div>
      </div>
    );
  }
}
